import React from 'react';
import { motion } from 'framer-motion';
import { Eye, Brain, Zap, Heart, ArrowRight, ArrowDown } from 'lucide-react';

const stages = [
  { name: 'Awareness', icon: Eye, color: 'cyan', desc: 'Traffic Sources' },
  { name: 'Consideration', icon: Brain, color: 'blue', desc: 'Lead Capture' },
  { name: 'Conversion', icon: Zap, color: 'purple', desc: 'Sales Process' },
  { name: 'Loyalty', icon: Heart, color: 'pink', desc: 'Retention & Referral' },
];

export default function CaseStudySection() {
  return (
    <section 
      id="case-study"
      data-testid="case-study-section"
      className="section-padding relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#030303]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(0,240,255,0.03)_0%,_transparent_70%)]" />
      
      <div className="container-custom relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-cyan-400 mb-4 block">
            The Complete System
          </span>
          <h2 
            data-testid="case-study-headline"
            className="text-4xl md:text-5xl font-bold text-white mb-6"
          >
            Your Marketing
            <br />
            <span className="gradient-text-cyan">Architecture</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-base">
            A visual representation of how all funnel stages work together 
            as a unified growth engine.
          </p>
        </motion.div>
        
        {/* Full Funnel Diagram */}
        <div className="max-w-4xl mx-auto">
          {/* Desktop View */}
          <div className="hidden md:block">
            <div className="relative">
              {/* Funnel Shape */}
              <svg className="w-full h-auto" viewBox="0 0 800 400" fill="none">
                {/* Funnel outline */}
                <motion.path
                  d="M100 50 L700 50 L550 350 L250 350 Z"
                  stroke="url(#funnelGradient)"
                  strokeWidth="2"
                  fill="url(#funnelFill)"
                  initial={{ pathLength: 0, opacity: 0 }}
                  whileInView={{ pathLength: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, ease: "easeOut" }}
                />
                
                {/* Stage dividers */}
                <motion.line x1="200" y1="125" x2="600" y2="125" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4 4" />
                <motion.line x1="275" y1="200" x2="525" y2="200" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4 4" />
                <motion.line x1="325" y1="275" x2="475" y2="275" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="4 4" />
                
                <defs>
                  <linearGradient id="funnelGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="#00F0FF" />
                    <stop offset="33%" stopColor="#3B82F6" />
                    <stop offset="66%" stopColor="#7000FF" />
                    <stop offset="100%" stopColor="#EC4899" />
                  </linearGradient>
                  <linearGradient id="funnelFill" x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(0, 240, 255, 0.05)" />
                    <stop offset="100%" stopColor="rgba(236, 72, 153, 0.05)" />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Stage Labels */}
              <div className="absolute inset-0">
                {stages.map((stage, index) => {
                  const topPositions = ['10%', '30%', '48%', '68%'];
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.5 + index * 0.2 }}
                      className="absolute left-1/2 transform -translate-x-1/2"
                      style={{ top: topPositions[index] }}
                    >
                      <div className={`flex items-center gap-3 px-4 py-2 rounded-full glass border border-${stage.color}-500/30`}>
                        <stage.icon className={`w-4 h-4 text-${stage.color}-400`} />
                        <span className="text-white text-sm font-medium">{stage.name}</span>
                        <span className="text-gray-500 text-xs">• {stage.desc}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
          
          {/* Mobile View */}
          <div className="md:hidden">
            <div className="space-y-4">
              {stages.map((stage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="relative"
                >
                  <div className={`glass rounded-xl p-4 border border-${stage.color}-500/20`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg bg-${stage.color}-500/10 flex items-center justify-center`}>
                        <stage.icon className={`w-5 h-5 text-${stage.color}-400`} />
                      </div>
                      <div>
                        <h4 className="text-white font-medium">{stage.name}</h4>
                        <p className="text-gray-500 text-xs">{stage.desc}</p>
                      </div>
                    </div>
                  </div>
                  
                  {index < stages.length - 1 && (
                    <div className="flex justify-center py-2">
                      <ArrowDown className="w-4 h-4 text-gray-600" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Key Insight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="mt-16 max-w-3xl mx-auto"
        >
          <div className="glass-heavy rounded-2xl p-8 border border-cyan-500/10">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-white mb-2">
                  How It All Connects
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  Each stage flows naturally into the next. Traffic becomes leads, leads become 
                  customers, customers become advocates who generate new traffic. This creates a 
                  self-reinforcing growth loop that compounds over time.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-cyan-400" />
                <ArrowRight className="w-4 h-4 text-gray-600" />
                <div className="w-3 h-3 rounded-full bg-blue-400" />
                <ArrowRight className="w-4 h-4 text-gray-600" />
                <div className="w-3 h-3 rounded-full bg-purple-400" />
                <ArrowRight className="w-4 h-4 text-gray-600" />
                <div className="w-3 h-3 rounded-full bg-pink-400" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
